import axios from "axios";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { baseurl, baseurImage } from "../../Baseurl";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import axiosInstance from "../../api/axiosInstance";

export default function EditInventory() {
  const location = useLocation();
  const navigate = useNavigate();
  const allBranchSettings = useSelector((state) => state.business.allBranchSettings);

  const [formData, setFormData] = useState({
    _id: "",
    barcode: "",
    name: "",
    substance: "",
    unit_of_measurement: "",
    company: "",
    quantity: "",
    expiration_date: "",
    cost: "",
    price: "",
    category: "",
    strength: "",
    image: "",
    vat: "No",
    vat_value: 0,
    reorder_level: "10",
    preferred_vendor_id: "",
    branch_id: "",
  });

  const [vatValue, setVatValue] = useState(0);

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    const drug = location.state?.drug;
    if (drug) {
      setFormData({ ...drug, reorder_level: drug.reorder_level ?? "10", preferred_vendor_id: drug.preferred_vendor_id ?? "" });
      if (drug.vat === "Yes") {
        setVatValue(Number(drug.vat_value) || 0);
      }
    }
    fetchCategories();
    fetchVendors();
  }, [location.state]);

  const fetchVatValue = async () => {
    try {
      const res = await axiosInstance.get(`getAllVat`);
      const value = Array.isArray(res?.data?.data) && res.data.data.length > 0
        ? Number(res.data.data[0].vat_value)
        : Number(res?.data?.vat_value ?? 0);
      const val = Number.isFinite(value) ? value : 0;
      setVatValue(val);
      setFormData(prev => ({ ...prev, vat_value: val }));
    } catch (e) {
      setVatValue(0);
      setFormData(prev => ({ ...prev, vat_value: 0 }));
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get(`getAllCategories`);
      if (res.data.success && Array.isArray(res.data.data)) {
        setCategories(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch categories", err);
    }
  };

  const fetchVendors = async () => {
    try {
      const res = await axiosInstance.get("getVendors");
      setVendors(res.data.data || []);
    } catch {}
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "vat" && value === "Yes") {
      fetchVatValue();
    }
    if (name === "vat" && value === "No") {
      setVatValue(0);
      setFormData(prev => ({ ...prev, vat_value: 0 }));
    }
  };

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const validate = () => {
    const err = {};
    if (!String(formData.name ?? "").trim()) err.name = "Name is required";
    if (!String(formData.substance ?? "").trim()) err.substance = "Substance is required";
    if (!String(formData.unit_of_measurement ?? "").trim()) err.unit_of_measurement = "Unit is required";
    if (!String(formData.company ?? "").trim()) err.company = "Company is required";
    if (!String(formData.quantity ?? "").trim()) err.quantity = "Quantity is required";
    if (!String(formData.expiration_date ?? "").trim()) err.expiration_date = "Expiration date is required";
    if (!formData.cost || isNaN(formData.cost)) err.cost = "Cost must be a number";
    if (!formData.price || isNaN(formData.price)) err.price = "Price must be a number";
    if (!formData.category) err.category = "Category is required";
    if (!String(formData.strength ?? "").trim()) err.strength = "Strength is required";
    return err;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const foundErrors = validate();
    if (Object.keys(foundErrors).length > 0) {
      setErrors(foundErrors);
      setSubmitted(false);
      return;
    }

    try {
      const data = new FormData();

      // Append all text fields
      Object.keys(formData).forEach((key) => {
        data.append(key, formData[key]);
      });

      // Append image only if selected
      if (imageFile) {
        data.append("image", imageFile);
      }

      const response = await axiosInstance.put(`updateDrug/${formData.id}`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success === true) {
        Swal.fire("Success!", "Inventory updated successfully!", "success").then(() => {
          navigate("/Admin/Pharmacy");
        });
      } else {
        alert("Update failed: " + response.data.message);
      }
    } catch (error) {
      console.error("Update error:", error);
      alert("Update failed: " + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="col-12">
          <div className="card table-card">
            <div className="tableHeader">
              <h5 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{ cursor: 'pointer', color: '#3f75c7', fontWeight: 'bold', fontSize: '1.5rem' }}
                  onClick={() => navigate(-1)}
                  title="Back"
                >
                  &larr;
                </span>
                Edit Inventory
              </h5>
            </div>

            {submitted && (
              <div className="alert alert-success">Inventory updated successfully!</div>
            )}

            <form onSubmit={handleSubmit} className="row g-3 px-3 py-2 mb-3">
              {/* Render all fields including barcode */}
              {["barcode", "name", "substance", "unit_of_measurement", "company", "quantity", "expiration_date", "cost", "price", "strength"].map((field) => (
                <div className="col-md-6" key={field}>
                  <label className="form-label">{field.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</label>
                  <input
                    type={field === "expiration_date" ? "date" : (field === "cost" || field === "price" ? "number" : "text")}
                    name={field}
                    className="form-control"
                    value={formData[field]}
                    onChange={handleChange}
                  />
                  {errors[field] && (
                    <p className="text-danger">{errors[field]}</p>
                  )}
                </div>
              ))}

              <div className="col-md-6">
                <label className="form-label">VAT</label>
                <select
                  name="vat"
                  value={formData.vat}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
                {formData.vat === "Yes" && (
                  <div className="form-text">Applied VAT: {vatValue}%</div>
                )}
              </div>

              {/* Category Dropdown */}
              <div className="col-md-6">
                <label className="form-label">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="form-control"
                  required
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-danger">{errors.category}</p>
                )}
              </div>



              {/* Reorder Level */}
              <div className="col-md-6">
                <label className="form-label">Reorder Level</label>
                <input
                  type="number" name="reorder_level"
                  value={formData.reorder_level} onChange={handleChange}
                  min="0" className="form-control"
                />
              </div>

              {/* Preferred Vendor */}
              <div className="col-md-6">
                <label className="form-label">Preferred Vendor</label>
                <select name="preferred_vendor_id" value={formData.preferred_vendor_id} onChange={handleChange} className="form-control">
                  <option value="">-- None --</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>

              {/* Branch */}
              {allBranchSettings && allBranchSettings.length > 1 && (
                <div className="col-md-6">
                  <label className="form-label">Branch</label>
                  <select
                    name="branch_id"
                    value={formData.branch_id || ""}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="">— None —</option>
                    {allBranchSettings.map((b, i) => (
                      <option key={b.id} value={b.id}>
                        Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Show Existing Image */}
              {formData.image && (
                <div className="col-md-6">
                  <label className="form-label">Current Image:</label><br />
                  <img
                    src={`${baseurImage}${formData.image}`}
                    alt="Current"
                    style={{ width: "100px", height: "100px", objectFit: "contain" }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div style={{ display: 'none', color: '#666', fontSize: '14px' }}>
                    Image not available
                  </div>
                </div>
              )}

              {/* Upload New Image */}
              <div className="col-md-6">
                <label className="form-label">Update Image (optional)</label>
                <input
                  type="file"
                  name="image"
                  className="form-control"
                  onChange={handleFileChange}
                  accept="image/*"
                />
              </div>

              <div className="col-12 text-center">
                <button type="submit" className="bgBtn">
                  Update Inventory
                </button>
              </div>
            </form>

          </div>
        </div>
      </div >
    </div >
  );
}
